type ApprovalState = {
  /** Funds on the customer's account, in cents. Shared across all of their invoices. */
  customerAvailableFunds?: number | string
  /**
   * The server's status for the invoice, deliberately not read here. `Short` describes the
   * invoice being under-funded, not the allocation being unpayable, so it doesn't clear
   * when the amounts are lowered to fit - gating approval on it strands the partial-approval
   * workflow the Short tab exists for, with no way back to an enabled button.
   */
  invoiceStatus?: string
}

const hasCustomerFunds = ({ customerAvailableFunds }: ApprovalState): boolean =>
  parseFloat(String(customerAvailableFunds)) > 0

/**
 * Whether the allocation actually fits is left to the validation schema, which checks it
 * against the per-rule balance, the customer's available funds and the invoice balance -
 * the same limits recon enforces - and clears as soon as the amounts are adjusted.
 */
export const canApprovePayments = (state: ApprovalState): boolean => hasCustomerFunds(state)

export const approveButtonLabel = (state: ApprovalState): string =>
  hasCustomerFunds(state) ? 'Approve Payments' : 'Insufficient Funds'

type BeneficiaryError = { key?: string; message?: string }

type ApprovalBeneficiary = {
  beneficiary?: {
    /** Banking pre-validation errors for the party, plus any EasyPay reference errors. */
    errors?: BeneficiaryError[]
    value?: { amountToPay?: number | string }
  }
}

/**
 * What the approval actually pays a beneficiary. A row with nothing outstanding contributes
 * no payment: `transformCreatePaymentRequest` drops it from the request body.
 */
const isBeneficiaryPayable = (b: ApprovalBeneficiary): boolean => Number(b?.beneficiary?.value?.amountToPay ?? 0) > 0

/**
 * The errors that have to stop an approval, gathered only from the beneficiaries it would
 * pay. A settled row is left out of the request body entirely, so a missing bank account on
 * its party says nothing about this approval and must not hold up the rest of the allocation.
 */
export const getBlockingBeneficiaryErrors = (beneficiaries: ApprovalBeneficiary[] = []): BeneficiaryError[] =>
  beneficiaries.filter(isBeneficiaryPayable).flatMap(b => b?.beneficiary?.errors ?? [])
